import React, { ComponentProps, KeyboardEventHandler, MouseEvent, useMemo, useRef, useState } from 'react'

import { Icon } from '@/shared/components'
import { DropdownContext, DropdownContextType } from '@/shared/components/Dropdown/DropdownContext'
import { scrollbarStyle } from '@/shared/styles'
import { pxToRem } from '@/shared/styles/themes'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import { Box, ButtonBase, ClickAwayListener, Fade, MenuList, Paper, Popper, styled, Tooltip, Typography } from '@mui/material'

export type NavItemSelectorProps = ComponentProps<typeof Box> & {
  children: React.ReactNode
  value: string
  fullHeight?: boolean
  onSelectItem: (value: string) => void
  isNavOpen: boolean
  isIconButton?: boolean
  icon?: IconDefinition
  slotProps?: {
    popper?: Omit<ComponentProps<typeof Popper>, 'open' | 'anchorEl'>
    tooltip?: Omit<ComponentProps<typeof Tooltip>, 'title'>
    button?: Omit<ComponentProps<typeof ButtonBase>, 'children'> & {
      children?: React.ReactNode
    }
  }
}

export const NavItemSelector: React.FC<NavItemSelectorProps> = ({ children, value, icon, onSelectItem, fullHeight, isNavOpen, isIconButton, slotProps, ...props }) => {
  const menuListRef = useRef<HTMLUListElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    setAnchorEl(event.currentTarget)
    setOpen(true)
  }

  const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      event.stopPropagation()

      setAnchorEl(event.currentTarget)
      setOpen(true)

      if (open && menuListRef) {
        menuListRef?.current?.focus()
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClose = (event: any) => {
    if (event?.target?.closest?.('.group-submenu')) {
      return 
    }

    event.preventDefault()
    event.stopPropagation()

    setTimeout(() => {
      setOpen(false)
    }, 100)
  }

  const defaultContext: DropdownContextType = useMemo(() => ({
    open,
    value,
    onSelectItem,
    setDropdownOpen: setOpen,
  }), [open, value, onSelectItem])

  const renderButtonContent = () => {
    if (slotProps?.button?.children) {
      return slotProps.button.children
    }

    return (
      <>
        {icon && <StyledNavIconContainer $isIconButton={isIconButton}>
          <StyledNavIcon icon={icon} />
        </StyledNavIconContainer>}
        {!isIconButton && isNavOpen && <StyledTitleContainer>
          <StyledDropdownValue variant="body1">
            {value}
          </StyledDropdownValue>
          <StyledIcon icon={faChevronDown} />
        </StyledTitleContainer>}
      </>
    )
  }

  const buttonElement = (
    <StyledButton
      disableTouchRipple
      onClick={handleClick}
      onKeyDown={onKeyDown}
      $open={open}
      $isNavOpen={isNavOpen}
      $isIconButton={isIconButton}
      {...(slotProps?.button || {})}
    >
      {renderButtonContent()}
    </StyledButton>
  )

  return (
    <DropdownContext.Provider value={defaultContext}>
      <StyledBox {...props}>
        {buttonElement}
      </StyledBox>
      <StyledPopper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        disablePortal={false}
        transition
        {...slotProps?.popper}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper sx={{ marginTop: pxToRem(4), marginBottom: pxToRem(4) }}>
              <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseDown" touchEvent="onTouchStart">
                <StyledMenuList ref={menuListRef} $fullHeight={fullHeight}>
                  {children}
                </StyledMenuList>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </StyledPopper>
    </DropdownContext.Provider>
  )
}

const StyledBox = styled(Box)`
  margin: ${pxToRem(7)} ${pxToRem(12)};
  max-width: ${pxToRem(206)};
`

export const StyledButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== '$open' && prop !== '$isIconButton' && prop !== '$isNavOpen',
}) <{ $open: boolean, $isIconButton?: boolean, $isNavOpen: boolean }>`
  border-radius: ${pxToRem(4)};
  padding: 0 ${(props) => !props.$isNavOpen || props.$isIconButton ? 0 : pxToRem(12)} 0 0 !important;
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, width 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  min-width: ${pxToRem(32)};
  max-width: 100%;
  width: 100%;
  padding-right: 0 !important;

  &.Mui-disabled {
    .MuiTypography-root {
      color: ${(props) => props.theme.palette.text.disabled};
    }
    
    .MuiSvgIcon-root {
      color: ${(props) => props.theme.palette.text.disabled};
    }
    
    box-shadow: none;
  }

  &:focus {
    background-color: rgba(255, 255, 255, 0.15);
  }
`

export const StyledPopper = styled(Popper)`
  z-index: ${(props) => props.theme.zIndex.appBar + 110};
  border-radius: ${pxToRem(4)};
  width: ${pxToRem(206)};
  .MuiPaper-root {
    overflow: visible;
  }
`

export const StyledDropdownContainer = styled('div')`
  display: inline-block;
`

export const StyledNavIconContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isIconButton',
}) <{ $isIconButton?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${pxToRem(32)};
  height: ${pxToRem(32)};
  background-color: ${(props) => props.$isIconButton ? 'transparent' : props.theme.palette.custom.sidebar.iconBackground};
  border-radius: ${pxToRem(4)};
`

export const StyledNavIcon = styled(Icon)`
  color: ${(props) => props.theme.palette.custom.sidebar.textDimColor};
  font-size: ${pxToRem(16)};
  width: ${pxToRem(32)};
`

export const StyledIcon = styled(Icon)`
  color: ${(props) => props.theme.palette.custom.sidebar.textDimColor};
  margin-left: ${props => props.theme.typography.pxToRem(8)};
  font-size: ${props => props.theme.typography.pxToRem(14)};
`

export const StyledTitleContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  margin-left: ${pxToRem(8)};
`

export const StyledDropdownValue = styled(Typography)`
  color: ${(props) => props.theme.palette.custom.sidebar.textDimColor};
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const StyledMenuList = styled(MenuList, {
  shouldForwardProp: (prop) => prop !== '$fullHeight',
}) <{ $fullHeight?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: ${props => props.theme.typography.pxToRem(4)};
  gap: 0;
  overflow: visible;
  
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  ${scrollbarStyle};

  li:first-of-type {
    &:after {
      border-radius: ${props => props.theme.typography.pxToRem(6)} ${props => props.theme.typography.pxToRem(6)} 0 0;
    }
  }

  li:last-of-type {
    &:after {
      border-radius: 0 0 ${props => props.theme.typography.pxToRem(6)} ${props => props.theme.typography.pxToRem(6)};
    }
  }
`
